import { useEffect, useState } from 'react';
import { BizButton } from '../../../Core/Components/Mini/BizButton';
import BizInput from '../../../Core/Components/Mini/BizInput';
import {
	useCreateDriverLog,
	useCreateDriverLogItem,
	useDeleteDriverLogItem,
	useGetDriverLogItems,
	useUpdateDriverLog,
	useUpdateDriverLogItem,
} from '../../../Core/Hooks/useTravelReport';
import type { StatusState } from '../../../Core/Hooks/useBizQuery';
import { DriverLog, DriverLogItem } from '../../../Core/Types/admin/timereport';
import {
	CreateDriverLogItemRequest,
	CreateDriverLogRequest,
	UpdateDriverLogItemRequest,
	UpdateDriverLogRequest,
} from '../../../Core/Types/timereport';
import './DriverLogForm.scss';

type DriverLogFormProps = {
	existingLog: DriverLog | null;
	onClose: () => void;
};

type DriverLogItemFormItem = Omit<DriverLogItem, 'driverLogId' | 'mileage'> & {
	localId: string;
	isNew: boolean;
};

const createLocalItemId = () => `driver-log-item-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const createEmptyDriverLogItem = (): DriverLogItemFormItem => ({
	localId: createLocalItemId(),
	id: 0,
	date: '',
	milageIn: 0,
	milageOut: 0,
	startPlace: '',
	endPlace: '',
	destination: '',
	company: '',
	contact: '',
	purpose: '',
	isDeleted: false,
	isNew: true,
});

const mapDriverLogItemToFormItem = (item: DriverLogItem): DriverLogItemFormItem => ({
	id: item.id,
	milageIn: item.milageIn,
	milageOut: item.milageOut,
	startPlace: item.startPlace,
	endPlace: item.endPlace,
	destination: item.destination,
	company: item.company,
	contact: item.contact,
	purpose: item.purpose,
	isDeleted: item.isDeleted,
	date: item.date?.slice(0, 10) ?? '',
	localId: `driver-log-item-${item.id}`,
	isNew: false,
});

const parseNumber = (value: string) => parseInt(value, 10) || 0;

const normalizePeriodValue = (period?: string) => {
	if (!period) {
		return '';
	}

	const trimmedPeriod = period.trim();
	if (/^\d{4}-\d{2}$/.test(trimmedPeriod)) {
		return trimmedPeriod;
	}

	if (/^\d{4}-\d{2}-\d{2}/.test(trimmedPeriod)) {
		return trimmedPeriod.slice(0, 7);
	}

	return trimmedPeriod;
};

const serializePeriodValue = (period: string) => {
	if (/^\d{4}-\d{2}$/.test(period)) {
		return `${period}-01`;
	}

	return period;
};

const getItemMileage = (item: Pick<DriverLogItemFormItem, 'milageIn' | 'milageOut'>) => item.milageOut - item.milageIn;

const getSaveErrorMessage = (error: unknown) => {
	if (error instanceof Error && error.message) {
		return error.message;
	}

	return 'Could not save the driver log.';
};

const DriverLogForm = ({ existingLog, onClose }: DriverLogFormProps) => {
	const { createAsync } = useCreateDriverLog();
	const { updateAsync } = useUpdateDriverLog();
	const { driverLogItems, statusState: itemStatus } = useGetDriverLogItems(existingLog?.id ?? 0);
	const { createAsync: createItemAsync } = useCreateDriverLogItem();
	const { updateAsync: updateItemAsync } = useUpdateDriverLogItem();
	const { removeAsync: removeItemAsync } = useDeleteDriverLogItem();

	const isEditing = existingLog !== null;

	const [form, setForm] = useState({
		period: normalizePeriodValue(existingLog?.period),
		licenseNumber: existingLog?.licenseNumber ?? '',
		mileageIn: existingLog?.mileageIn ?? 0,
		mileageOut: existingLog?.mileageOut ?? 0,
	});
	const [items, setItems] = useState<DriverLogItemFormItem[]>([]);
	const [saveStatus, setSaveStatus] = useState<StatusState>('INITIAL');
	const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

	useEffect(() => {
		setForm({
			period: normalizePeriodValue(existingLog?.period),
			licenseNumber: existingLog?.licenseNumber ?? '',
			mileageIn: existingLog?.mileageIn ?? 0,
			mileageOut: existingLog?.mileageOut ?? 0,
		});
		setItems([]);
		setSaveStatus('INITIAL');
		setSaveErrorMessage(null);
	}, [existingLog]);

	useEffect(() => {
		if (!isEditing) {
			setItems([]);
			return;
		}

		if (itemStatus === 'SUCCESS') {
			setItems((driverLogItems ?? []).map(mapDriverLogItemToFormItem));
		}
	}, [driverLogItems, isEditing, itemStatus]);

	const updateField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
		setForm((prev) => ({ ...prev, [key]: value }));
	};

	const updateItemField = <K extends keyof Omit<DriverLogItemFormItem, 'localId' | 'isNew'>>(
		localId: string,
		key: K,
		value: DriverLogItemFormItem[K]
	) => {
		setItems((prev) => prev.map((item) => (item.localId === localId ? { ...item, [key]: value } : item)));
	};

	const handleAddItem = () => {
		setItems((prev) => [...prev, createEmptyDriverLogItem()]);
	};

	const handleDeleteItem = (localId: string) => {
		setItems((prev) =>
			prev.flatMap((item) => {
				if (item.localId !== localId) {
					return [item];
				}

				if (item.isNew) {
					return [];
				}

				return [{ ...item, isDeleted: true }];
			})
		);
	};

	const toCreateDriverLogItemRequest = (item: DriverLogItemFormItem, driverLogId: number): CreateDriverLogItemRequest => ({
		driverLogId,
		date: item.date,
		mileage: getItemMileage(item),
		milageIn: item.milageIn,
		milageOut: item.milageOut,
		startPlace: item.startPlace,
		endPlace: item.endPlace,
		destination: item.destination,
		company: item.company,
		contact: item.contact,
		purpose: item.purpose,
	});

	const toUpdateDriverLogItemRequest = (item: DriverLogItemFormItem, driverLogId: number): UpdateDriverLogItemRequest => ({
		id: item.id,
		driverLogId,
		date: item.date,
		mileage: getItemMileage(item),
		milageIn: item.milageIn,
		milageOut: item.milageOut,
		startPlace: item.startPlace,
		endPlace: item.endPlace,
		destination: item.destination,
		company: item.company,
		contact: item.contact,
		purpose: item.purpose,
		isDeleted: false,
	});

	const syncDriverLogItems = async (driverLogId: number) => {
		const itemsToDelete = items.filter((item) => item.isDeleted && !item.isNew && item.id > 0);
		const activeItems = items.filter((item) => !item.isDeleted);
		const itemsToCreate = activeItems.filter((item) => item.isNew || item.id <= 0);
		const itemsToUpdate = activeItems.filter((item) => !item.isNew && item.id > 0);

		await Promise.all(itemsToDelete.map((item) => removeItemAsync(item.id, driverLogId)));
		await Promise.all(itemsToCreate.map((item) => createItemAsync(toCreateDriverLogItemRequest(item, driverLogId))));
		await Promise.all(itemsToUpdate.map((item) => updateItemAsync(toUpdateDriverLogItemRequest(item, driverLogId))));
	};

	const handleSave = async () => {
		setSaveStatus('LOADING');
		setSaveErrorMessage(null);

		try {
			let driverLogId = existingLog?.id ?? 0;
			const periodValue = serializePeriodValue(form.period);

			if (isEditing) {
				const data: UpdateDriverLogRequest = {
					id: existingLog.id,
					...form,
					period: periodValue,
				};
				await updateAsync(data);
			} else {
				const data: CreateDriverLogRequest = { ...form, period: periodValue };
				const createdLog = await createAsync(data);
				if (!createdLog?.id) {
					throw new Error('The driver log was created without a valid id.');
				}
				driverLogId = createdLog.id;
			}

			await syncDriverLogItems(driverLogId);
			setSaveStatus('SUCCESS');
			onClose();
		} catch (error) {
			setSaveStatus('ERROR');
			setSaveErrorMessage(getSaveErrorMessage(error));
		}
	};

	const visibleItems = items.filter((item) => !item.isDeleted);
	const deletedItemsCount = items.length - visibleItems.length;
	const totalMileage = form.mileageOut - form.mileageIn;
	const serviceMileage = visibleItems.reduce((sum, item) => sum + getItemMileage(item), 0);
	const privateMileage = totalMileage - serviceMileage;

	return (
		<div className='biz-driver-log-form'>
			<div className='biz-driver-log-form__title'>{isEditing ? 'Edit Driver Log' : 'New Driver Log'}</div>

			<div className='biz-driver-log-form__fields'>
				<div className='biz-driver-log-form__field'>
					<span className='biz-driver-log-form__label'>Month</span>
					<BizInput type='month' value={form.period} onChange={(e) => updateField('period', e.target.value)} />
				</div>

				<div className='biz-driver-log-form__field'>
					<span className='biz-driver-log-form__label'>License Number</span>
					<BizInput value={form.licenseNumber} onChange={(e) => updateField('licenseNumber', e.target.value)} />
				</div>

				<div className='biz-driver-log-form__row'>
					<div className='biz-driver-log-form__field'>
						<span className='biz-driver-log-form__label'>Mileage In</span>
						<BizInput
							type='number'
							value={String(form.mileageIn)}
							onChange={(e) => updateField('mileageIn', parseNumber(e.target.value))}
							min='0'
						/>
					</div>
					<div className='biz-driver-log-form__field'>
						<span className='biz-driver-log-form__label'>Mileage Out</span>
						<BizInput
							type='number'
							value={String(form.mileageOut)}
							onChange={(e) => updateField('mileageOut', parseNumber(e.target.value))}
							min='0'
						/>
					</div>
				</div>
			</div>

			<div className='biz-driver-log-form__summary'>
				<div className='biz-driver-log-form__summary-item'>
					<span className='biz-driver-log-form__summary-label'>Total vehicle mileage</span>
					<span className='biz-driver-log-form__summary-value'>{totalMileage}</span>
				</div>
				<div className='biz-driver-log-form__summary-item'>
					<span className='biz-driver-log-form__summary-label'>Service mileage</span>
					<span className='biz-driver-log-form__summary-value'>{serviceMileage}</span>
				</div>
				<div className='biz-driver-log-form__summary-item'>
					<span className='biz-driver-log-form__summary-label'>Private mileage</span>
					<span className='biz-driver-log-form__summary-value'>{privateMileage}</span>
				</div>
			</div>

			<div className='biz-driver-log-form__section'>
				<div className='biz-driver-log-form__section-header'>
					<div>
						<div className='biz-driver-log-form__subtitle'>Log Items</div>
						<div className='biz-driver-log-form__help'>
							Add trips and service mileage rows. Item changes are saved together with the driver log.
						</div>
					</div>
					<BizButton
						text='Add Item'
						onClick={handleAddItem}
						icon='fa-solid fa-plus'
						disabled={saveStatus === 'LOADING'}
					/>
				</div>

				{isEditing && itemStatus === 'LOADING' && (
					<div className='biz-driver-log-form__message'>Loading log items...</div>
				)}
				{isEditing && itemStatus === 'ERROR' && (
					<div className='biz-driver-log-form__error'>Could not load log items.</div>
				)}
				{deletedItemsCount > 0 && (
					<div className='biz-driver-log-form__message'>
						{deletedItemsCount} item{deletedItemsCount === 1 ? '' : 's'} will be deleted when you save.
					</div>
				)}
				{visibleItems.length === 0 && itemStatus !== 'LOADING' && (
					<div className='biz-driver-log-form__empty'>No log items added yet.</div>
				)}

				{visibleItems.length > 0 && (
					<div className='biz-driver-log-form__items-table-wrap'>
						<table className='biz-driver-log-form__items-table'>
							<thead>
								<tr>
									<th>Date</th>
									<th>In</th>
									<th>Out</th>
									<th>Service</th>
									<th>Start</th>
									<th>End</th>
									<th>Destination</th>
									<th>Company</th>
									<th>Contact</th>
									<th>Purpose</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{visibleItems.map((item) => (
									<tr key={item.localId}>
										<td>
											<BizInput
												type='date'
												value={item.date}
												onChange={(e) => updateItemField(item.localId, 'date', e.target.value)}
											/>
										</td>
										<td>
											<BizInput
												type='number'
												value={String(item.milageIn)}
												onChange={(e) =>
													updateItemField(item.localId, 'milageIn', parseNumber(e.target.value))
												}
												min='0'
											/>
										</td>
										<td>
											<BizInput
												type='number'
												value={String(item.milageOut)}
												onChange={(e) =>
													updateItemField(item.localId, 'milageOut', parseNumber(e.target.value))
												}
												min='0'
											/>
										</td>
										<td>
											<div className='biz-driver-log-form__calculated-value'>{getItemMileage(item)}</div>
										</td>
										<td>
											<BizInput
												value={item.startPlace}
												onChange={(e) => updateItemField(item.localId, 'startPlace', e.target.value)}
											/>
										</td>
										<td>
											<BizInput
												value={item.endPlace}
												onChange={(e) => updateItemField(item.localId, 'endPlace', e.target.value)}
											/>
										</td>
										<td>
											<BizInput
												value={item.destination}
												onChange={(e) => updateItemField(item.localId, 'destination', e.target.value)}
											/>
										</td>
										<td>
											<BizInput
												value={item.company}
												onChange={(e) => updateItemField(item.localId, 'company', e.target.value)}
											/>
										</td>
										<td>
											<BizInput
												value={item.contact}
												onChange={(e) => updateItemField(item.localId, 'contact', e.target.value)}
											/>
										</td>
										<td>
											<BizInput
												value={item.purpose}
												onChange={(e) => updateItemField(item.localId, 'purpose', e.target.value)}
											/>
										</td>
										<td className='biz-driver-log-form__table-action'>
											<BizButton
												text='Delete'
												onClick={() => handleDeleteItem(item.localId)}
												disabled={saveStatus === 'LOADING'}
											/>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{saveErrorMessage && <div className='biz-driver-log-form__error'>{saveErrorMessage}</div>}

			<div className='biz-driver-log-form__actions'>
				<BizButton text={isEditing ? 'Update' : 'Create'} onClick={handleSave} state={saveStatus} icon='far fa-save' />
				<BizButton text='Cancel' onClick={onClose} disabled={saveStatus === 'LOADING'} />
			</div>
		</div>
	);
};

export default DriverLogForm;
